import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Copy, Printer, FileText, ClipboardCheck } from "lucide-react";

export const CATEGORY_LABEL = { tardiness: "Tardiness", undertime: "Undertime", awol: "AWOL" };

const FIELD_LABEL = {
    details: "Dates and details", response_days: "Days to respond", rule_reference: "Company rule it breaks",
    offense_no: "Which offense", nte_date: "Date of the Notice to Explain", explanation_summary: "Employee's explanation, in short",
    decision: "Decision", effectivity: "Effective on", absent_since: "Absent since", report_by: "Must report on or before",
    hr_contact: "HR contact (phone or email)", address_sent_to: "Sent to (last known address)", prior_notice_note: "Earlier notices (optional)",
};
const DATE_FIELDS = new Set(["nte_date", "effectivity", "absent_since", "report_by"]);
const LONG_FIELDS = new Set(["details", "explanation_summary", "decision", "prior_notice_note"]);

const longDate = (iso) => {
    if (!iso) return "";
    const d = new Date(`${iso}T00:00:00`);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

/* Range picker that pulls the dates behind a case from the attendance records. */
export function AttendanceFacts({ employeeId, category, onFacts, defaultStart = "", defaultEnd = "" }) {
    const [start, setStart] = useState(defaultStart);
    const [end, setEnd] = useState(defaultEnd);
    const [busy, setBusy] = useState(false);
    if (!category || !CATEGORY_LABEL[category]) return null;
    const pull = async () => {
        if (!employeeId) return toast.error("Choose the employee first");
        if (!start || !end) return toast.error("Pick the first and last date to look at");
        setBusy(true);
        try {
            const { data } = await api.get("/hr-docs/attendance-facts", { params: { employee_id: employeeId, start, end, category } });
            if (!data.count) { toast.info("No matching days in that range"); onFacts("", { start, end }); return; }
            onFacts(data.lines.join("\n"), { start, end, note: data.note });
            toast.success(`${data.count} day${data.count === 1 ? "" : "s"} found`);
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <div className="rounded-md border border-border p-2 space-y-2" data-testid="attendance-facts">
            <div className="text-xs font-medium">Fill the dates from attendance</div>
            <div className="flex flex-wrap items-end gap-2">
                <div><Label className="text-xs">From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="facts-start" /></div>
                <div><Label className="text-xs">To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="facts-end" /></div>
                <button type="button" className="h-9 px-3 rounded-md border border-border text-sm disabled:opacity-60" disabled={busy} onClick={pull} data-testid="facts-pull">Pull from attendance</button>
            </div>
        </div>
    );
}

export default function CaseDocumentDialog({ open, onClose, kase, employeeName, onIssued }) {
    const [catalog, setCatalog] = useState(null);
    const [templateId, setTemplateId] = useState("");
    const [showAll, setShowAll] = useState(false);
    const [fields, setFields] = useState({});
    const [doc, setDoc] = useState(null);       // { body, missing, warnings }
    const [text, setText] = useState("");       // the editable copy
    const [edited, setEdited] = useState(false);
    const [note, setNote] = useState("");
    const seq = useRef(0);

    useEffect(() => {
        if (!open || catalog) return;
        api.get("/hr-docs/templates").then((r) => setCatalog(r.data)).catch((e) => toast.error(formatErrorDetail(e.response?.data?.detail)));
    }, [open, catalog]);

    const list = useMemo(() => {
        if (!catalog) return [];
        if (showAll || !kase?.category) return catalog.items;
        return catalog.items.filter((t) => t.category === kase.category || t.category === "any");
    }, [catalog, showAll, kase]);

    // Start on the case's own first notice, with the case description as the details.
    useEffect(() => {
        if (!open || !catalog || !kase) return;
        const first = catalog.items.find((t) => t.category === kase.category && t.kind === "first_notice") || catalog.items[0];
        setTemplateId(first?.id || "");
        setFields({ details: kase.description || "" });
        setEdited(false); setDoc(null); setText(""); setNote("");
    }, [open, catalog, kase]);

    const tpl = catalog?.items.find((t) => t.id === templateId);

    // Re-render whenever the template or a field changes (typing is debounced).
    useEffect(() => {
        if (!open || !tpl || !kase) return undefined;
        const mine = ++seq.current;
        const t = setTimeout(async () => {
            try {
                const sent = {};
                Object.entries(fields).forEach(([k, v]) => { sent[k] = DATE_FIELDS.has(k) ? longDate(v) : v; });
                const { data } = await api.post("/hr-docs/render", { template_id: tpl.id, employee_id: kase.employee_id, fields: sent });
                if (mine !== seq.current) return;
                setDoc(data);
                if (!edited) setText(data.body);
            } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
        }, 350);
        return () => clearTimeout(t);
    }, [open, tpl, fields, kase, edited]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!kase) return null;
    const set = (k, v) => setFields((f) => ({ ...f, [k]: v }));

    const copy = async () => { try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { toast.error("Could not copy. Select the text and copy it."); } };
    const print = () => {
        const w = window.open("", "_blank", "noopener,width=800,height=900");
        if (!w) return toast.error("The print window was blocked. Allow pop-ups and try again.");
        const esc = text.replace(/&/g, "&amp;").replace(/</g, "&lt;");
        w.document.write(`<!doctype html><title>${tpl?.title || "Document"}</title><style>body{font:14px/1.5 Georgia,serif;margin:2.2cm}pre{white-space:pre-wrap;font:inherit}</style><pre>${esc}</pre>`);
        w.document.close(); w.focus(); w.print();
    };
    const issued = async () => {
        if (doc?.missing?.length && !window.confirm(`These parts are still blank: ${doc.missing.join(", ")}. Log it as issued anyway?`)) return;
        try {
            await api.post(`/relations/${kase.id}/actions`, { type: "notice", description: `Issued: ${tpl.title}` });
            toast.success("Logged on the case as issued");
            onIssued && onIssued();
            onClose();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="case-doc-dialog">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="size-4 text-primary" /> Prepare a document for {employeeName}</DialogTitle></DialogHeader>
                {!catalog ? <p className="text-sm">Loading templates…</p> : (
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <div>
                                <Label>Document</Label>
                                <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={templateId} onChange={(e) => { setTemplateId(e.target.value); setEdited(false); }} data-testid="doc-template">
                                    {list.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                                </select>
                                {kase.category && (
                                    <label className="text-xs flex items-center gap-1 mt-1"><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> Show every template</label>
                                )}
                                {tpl && <p className="text-xs text-muted-foreground mt-1">{tpl.summary}</p>}
                            </div>
                            {tpl?.fields?.includes("details") && ["tardiness", "undertime", "awol"].includes(tpl.category === "any" ? kase.category : tpl.category) && (
                                <AttendanceFacts employeeId={kase.employee_id} category={tpl.category === "any" ? kase.category : tpl.category}
                                    onFacts={(txt, meta) => { if (txt) { set("details", txt); setEdited(false); } if (meta?.note) setNote(meta.note); }} />
                            )}
                            {note && <p className="text-xs text-amber-700">{note}</p>}
                            {(tpl?.fields || []).map((k) => (
                                <div key={k}>
                                    <Label className="text-xs">{FIELD_LABEL[k] || k}</Label>
                                    {k === "offense_no" ? (
                                        <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={fields[k] || "first"} onChange={(e) => set(k, e.target.value)}>
                                            <option value="first">First</option><option value="second">Second</option><option value="third">Third</option><option value="final">Final</option>
                                        </select>
                                    ) : LONG_FIELDS.has(k) ? (
                                        <Textarea rows={k === "details" ? 5 : 2} value={fields[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`doc-field-${k}`} />
                                    ) : (
                                        <Input type={DATE_FIELDS.has(k) ? "date" : "text"} value={fields[k] || ""} onChange={(e) => set(k, e.target.value)}
                                            placeholder={k === "response_days" ? "5" : k === "rule_reference" ? "the Company's attendance policy" : ""} data-testid={`doc-field-${k}`} />
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label>Preview (you can edit the text)</Label>
                                {edited && <button type="button" className="text-xs underline" onClick={() => { setEdited(false); doc && setText(doc.body); }}>Reset to the template</button>}
                            </div>
                            <Textarea className="font-mono text-xs min-h-[420px]" value={text} onChange={(e) => { setText(e.target.value); setEdited(true); }} data-testid="doc-preview" />
                            {doc?.warnings?.map((w) => <p key={w} className="text-xs text-amber-700" data-testid="doc-warning">{w}</p>)}
                            {doc?.missing?.length > 0 && <p className="text-xs text-amber-700" data-testid="doc-missing">Still blank: {doc.missing.map((k) => FIELD_LABEL[k] || k).join(", ")}</p>}
                            <p className="text-[11px] text-muted-foreground">A draft for HR to review. Check the wording with the company&apos;s counsel before issuing. A first notice should give the employee at least {catalog.min_response_days} calendar days to answer.</p>
                        </div>
                    </div>
                )}
                <DialogFooter className="gap-2">
                    <button className="h-9 px-3 rounded-md border border-border text-sm flex items-center gap-1" onClick={copy} data-testid="doc-copy"><Copy className="size-4" /> Copy</button>
                    <button className="h-9 px-3 rounded-md border border-border text-sm flex items-center gap-1" onClick={print} data-testid="doc-print"><Printer className="size-4" /> Print</button>
                    <button className="h-9 px-3 rounded-md bg-primary text-white text-sm flex items-center gap-1" onClick={issued} data-testid="doc-issued"><ClipboardCheck className="size-4" /> Log as issued</button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
